import * as R from 'remeda';
import type { Prettify } from '../../../../lib/typeUtils';

export type Point2D = {
  x: number;
  y: number;
};

/** Intentional alias — both are `{x, y}`. Vector2D signals directional/delta semantics. */
export type Vector2D = Point2D;

export type Rectangle2D = Prettify<
  Point2D & {
    width: number;
    height: number;
  }
>;

export type Dimensions = {
  width: number;
  height: number;
};

export type Padding = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

// Point Utilities
export const toDOMPoint = (point: Point2D) => new DOMPoint(point.x, point.y);

export const toPoint2D = (point: DOMPoint): Point2D => ({
  x: point.x,
  y: point.y,
});

export const matrixTransform =
  (matrix: DOMMatrixReadOnly) => (point: Point2D) =>
    R.pipe(point, toDOMPoint, p => p.matrixTransform(matrix), toPoint2D);

// Vector utilities
export const getVectorSize = (vector: Vector2D): number =>
  Math.sqrt(vector.x ** 2 + vector.y ** 2);

export const getVectorDifference = (v1: Vector2D, v2: Vector2D): Vector2D => ({
  x: v1.x - v2.x,
  y: v1.y - v2.y,
});

export const origin = (): Vector2D => ({
  x: 0,
  y: 0,
});

export const unit = (): Vector2D => ({
  x: 1,
  y: 0,
});

// Rectangle Utilities

/**
 * Normalizes a rectangle defined by two corner points.
 * Ensures the result has positive width/height with (x,y) as the top-left corner,
 * regardless of which direction the rectangle was drawn.
 */
export const normalizeRectangle = (
  start: Point2D,
  end: Point2D
): Rectangle2D => ({
  x: Math.min(start.x, end.x),
  y: Math.min(start.y, end.y),
  width: Math.abs(end.x - start.x),
  height: Math.abs(end.y - start.y),
});

/** Checks if a point is inside a rectangle */
export const isPointInRectangle = (
  point: Point2D,
  rectangle: Rectangle2D
): boolean =>
  point.x >= rectangle.x &&
  point.x <= rectangle.x + rectangle.width &&
  point.y >= rectangle.y &&
  point.y <= rectangle.y + rectangle.height;

/** Checks if two rectangles intersect */
export const doRectanglesIntersect = (
  r1: Rectangle2D,
  r2: Rectangle2D
): boolean =>
  r1.x + r1.width >= r2.x &&
  r1.x <= r2.x + r2.width &&
  r1.y + r1.height >= r2.y &&
  r1.y <= r2.y + r2.height;

/** Calculates the area of a rectangle */
export const getRectangleArea = (r: Rectangle2D): number => r.width * r.height;

/** Returns the intersection rectangle of two rectangles, or null if they don't intersect */
export const getIntersectingRectangle = (
  r1: Rectangle2D,
  r2: Rectangle2D
): Rectangle2D | null => {
  const x = Math.max(r1.x, r2.x);
  const y = Math.max(r1.y, r2.y);
  const width = Math.min(r1.x + r1.width, r2.x + r2.width) - x;
  const height = Math.min(r1.y + r1.height, r2.y + r2.height) - y;

  if (width <= 0 || height <= 0) {
    return null;
  }

  return { x, y, width, height };
};

/** Calculates the intersection area of two rectangles */
export const getRectangleIntersectionArea = (
  r1: Rectangle2D,
  r2: Rectangle2D
): number => {
  const intersection = getIntersectingRectangle(r1, r2);
  return intersection ? getRectangleArea(intersection) : 0;
};

/** Extracts the four corner points of a rectangle (top-left, top-right, bottom-left, bottom-right) */
export const getRectangleCorners = (
  r: Rectangle2D
): [Point2D, Point2D, Point2D, Point2D] => [
  { x: r.x, y: r.y },
  { x: r.x + r.width, y: r.y },
  { x: r.x, y: r.y + r.height },
  { x: r.x + r.width, y: r.y + r.height },
];

/** Creates an axis-aligned bounding box from a set of points */
export const getWrappingRectangle = (points: Point2D[]): Rectangle2D => {
  const xs = points.map(p => p.x);
  const ys = points.map(p => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
};

/**
 * Finds the first rectangle in the collection that contains the given point.
 * Searches in reverse order to prioritize topmost/last elements.
 * @returns The matching rectangle, or null if no rectangle contains the point
 */
export const findRectangleAtPoint = <T extends Rectangle2D>(
  point: Point2D,
  rectangles: readonly T[]
): T | null => {
  for (let i = rectangles.length - 1; i >= 0; i -= 1) {
    const rect = rectangles[i];
    if (rect && isPointInRectangle(point, rect)) {
      return rect;
    }
  }
  return null;
};
